import React, { useMemo } from 'react'
import {getServiceTranslation, minutesToHoursAndMinutes, numberToCurrencyString} from "../../Utils/helpers"
import {Tooltip, Typography} from "@mui/material"
import {Currency} from "../Currency"
import {TextButton} from "../Button"
import {useTranslation} from "react-i18next"
import {makeStyles} from "tss-react/mui"
import {Theme} from "@mui/material/styles"
import {AccordionItem} from "./AccordionItem"
import {useSelector} from "react-redux"
import {RootState} from "../../Redux"
import {get} from "lodash"
import {TRAFICOM_FEE_CATEGORY_ID} from "../../Utils/const"

const useStyles = makeStyles()((theme: Theme) => ({
    row: {
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'space-between',
    },
    accordionText: {
        fontSize: theme.typography.pxToRem(14),
        color: theme.palette.grey[900],
    },
}))

function ServiceInfo({timeUntilStartMinutes, handleOpenServiceDialog}) {
    const { t } = useTranslation()
    const { classes } = useStyles()

    const checkIn = useSelector((state: RootState) => state.checkIn);
    const ui = useSelector((state: RootState) => state.ui);

    const bookingOptions = ui.settings?.booking?.options;

    const {
        cancelBefore,
        changeBefore,
        allowChanges,
        allowCashPayment,
        allowOnlinePayment,
        checkInOpenBefore,
        prePaymentBefore,
        taskState,
        priceInfo,
    } = useMemo(() => ({
        cancelBefore: bookingOptions?.cancelBefore,
        changeBefore: bookingOptions?.changeBefore,
        allowChanges: bookingOptions?.allowChanges || false,
        allowCashPayment: bookingOptions?.allowCashPayment || false,
        allowOnlinePayment: bookingOptions?.allowOnlinePayment || false,
        checkInOpenBefore: bookingOptions?.checkInOpenBefore || 30,
        prePaymentBefore: bookingOptions?.prePaymentBefore,
        taskState: checkIn.checkIn?.sale?.taskState,
        priceInfo: checkIn.checkIn?.prices,
    }), [
        bookingOptions.cancelBefore,
        bookingOptions.changeBefore,
        bookingOptions.allowChanges,
        bookingOptions.allowCashPayment,
        bookingOptions.allowOnlinePayment,
        bookingOptions.checkInOpenBefore,
        bookingOptions.prePaymentBefore,
        checkIn.checkIn?.sale?.taskState,
        checkIn.checkIn?.prices,
    ]);

    const traficomFee = useMemo(() => {
        return checkIn?.checkIn?.reservation?.tasks?.map(
            (task) => task?.basket?.items?.map(
                (basketItem) => basketItem?.categoryId === TRAFICOM_FEE_CATEGORY_ID ? basketItem?.price?.value : null
            ).filter(fee => !!fee).reduce((total, fee) => total + Number.parseFloat(fee), 0)
        ).reduce((total, fee) => total + Number.parseFloat(fee), 0)
    }, [checkIn?.checkIn?.reservation?.tasks]);

    // Intervals as minutes
    const interval = {
        checkIn: checkInOpenBefore,
        serviceChange: changeBefore,
        dateChange: changeBefore,
        cancel: cancelBefore,
        prePayment: prePaymentBefore,
    }

    const isFullyPaid = checkIn.checkIn?.sale?.totalPrice?.vatIncluded > 0 && checkIn.checkIn?.sale?.remainingPrice?.vatIncluded <= 0
    const isFullyRefunded = checkIn.checkIn?.sale?.totalPrice?.vatIncluded <= 0 && checkIn.checkIn?.sale?.remainingPrice?.vatIncluded <= 0 && checkIn.checkIn.sale.tasks?.length > 0
    const isPaymentInProgress = (isFullyPaid || isFullyRefunded) && (checkIn.checkIn.sale.hasPendingOnlinePayment || checkIn.checkIn?.sale?.remainingPrice?.vatIncluded != 0)

    const isCancelled = checkIn.checkIn?.sale?.tasks?.length < 1 || get(checkIn, 'checkIn.sale.tasks[0].event.eventType') <= 0
    const isCheckedIn = taskState === 3
    const inspectIsReady = taskState === 4

    const serviceChangeDisabled = !timeUntilStartMinutes || timeUntilStartMinutes < interval.serviceChange


    const renderServiceInfo = () => {
        if (isPaymentInProgress) {
            return <div><b>
                {isFullyRefunded ? t('serviceCardContainer.refundInProgress') : t('serviceCardContainer.paymentInProgress')}
            </b></div>
        } else if (isFullyRefunded) {
            return <div><b>
                {t('serviceCardContainer.paymentRefunded')}
            </b></div>
        } else if (isFullyPaid) {
            return <div>
                {t('serviceCardContainer.paymentFulfilled',
                    {
                        price: numberToCurrencyString(checkIn.checkIn?.sale?.totalPrice?.vatIncluded),
                        vat: numberToCurrencyString(checkIn.checkIn?.sale?.totalPrice?.vat)
                    })}
            </div>
        } else if (isCancelled) {
            return <div>
                {t('serviceCardContainer.reservationCancelled')}
            </div>
        }
        const helper = t('serviceCardContainer.changeServiceTooltip', { time: minutesToHoursAndMinutes(interval.dateChange) })
        return (
            <div>
                {
                    allowOnlinePayment && <Typography className={classes.accordionText}>
                        {t('serviceCardContainer.prepaidPrice')}{' '}
                        <Currency value={priceInfo?.prepaid?.value ?? checkIn.checkIn?.sale?.totalPrice?.vatIncluded} />
                    </Typography>
                }
                {
                    allowCashPayment && <Typography className={classes.accordionText}>
                        {t('serviceCardContainer.normalPrice')}{' '}
                        <Currency value={priceInfo?.online?.value ?? checkIn.checkIn?.sale?.totalPrice?.vatIncluded} />
                    </Typography>
                }
                {traficomFee > 0 ? (
                    <Typography className={classes.accordionText}>
                        {t('rootContainer.priceIncludesTraficomFee', { price: numberToCurrencyString(traficomFee) })}
                    </Typography>
                ) : null}
                {allowChanges && ! isCheckedIn && ! inspectIsReady &&
                    <div className={classes.row}>
                        <Tooltip arrow placement="bottom-start" title={helper} aria-label={helper}>
                            <div>
                                <TextButton
                                    onClick={handleOpenServiceDialog}
                                    disabled={serviceChangeDisabled}
                                >
                                    {t('serviceCardContainer.changeService')}
                                </TextButton>
                            </div>
                        </Tooltip>
                    </div>
                }
            </div>
        )
    }

    const serviceInfo = get(
        checkIn,
        'checkIn.sale.tasks[0].basket.items[0]',
    )
    const reservedServiceInfo = get(
        checkIn,
        'checkIn.reservation.tasks[0].basket.items[0]',
    )
    const serviceName = getServiceTranslation('name', serviceInfo ?? reservedServiceInfo)

    return (
        <AccordionItem
            heading={t('common.service')}
            subHeading={serviceName}
            content={renderServiceInfo()}
        />
    )
}

export default ServiceInfo
